import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaChevronUp,
  FaEnvelope,
  FaExternalLinkAlt,
  FaStar,
  FaTable,
  FaTh,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import HomeClampedText from '../../components/HomeClampedText';
import InfrastructureLeasingDealAcceptModal from '../../components/infrastructureLeasing/InfrastructureLeasingDealAcceptModal';
import InfrastructureLeasingDealRejectModal from '../../components/infrastructureLeasing/InfrastructureLeasingDealRejectModal';
import InfrastructureLeasingRejectionDetailsModal from '../../components/infrastructureLeasing/InfrastructureLeasingRejectionDetailsModal';
import InfrastructureLeasingRespondChoiceModal from '../../components/infrastructureLeasing/InfrastructureLeasingRespondChoiceModal';
import InfrastructureLeasingRequirementQuotationModal from '../../components/infrastructureLeasing/InfrastructureLeasingRequirementQuotationModal';
import {
  fetchSellerListingEnquiries,
  postSellerCancelAcceptedInfrastructureDeal,
  postSellerCancelPaymentDueUnpaidDeal,
  postSellerTerminateLeaseEarly,
  postSellerRequestMutualLeaseTermination,
  postSellerConfirmMutualLeaseTermination,
  postSellerWithdrawMutualLeaseTermination,
  postSellerRejectMutualLeaseTermination,
  postUnwindPendingSignatureDeal,
  getSellerCommercialDocuments,
  downloadSellerCommercialDocument,
} from '../../services/infrastructureLeasingListing.service';
import { formatInfrastructureLeasingUrgency } from '../../constants/infrastructureLeasingUrgencyOptions';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/infrastructure_search_listings.scss';
import '../../assets/styles/infrastructure_leasing_seller_listing_enquiries.scss';

const LAYOUT_KEY = 'ile_seller_listing_enquiries_layout';

/** Phase 10: show seller “cancel before buyer sign” when API row matches eligibility (failClosed on server too). */
function sellerPhase10EligibleAcceptedPreBuyer(item) {
  if (String(item.dealStatus || '').toLowerCase() !== 'accepted') return false;
  if (item.buyerPaymentCompleted) return false;
  if (item.buyerPostSaleStage) return false;
  if (item.agreementBuyerSignedAt != null && String(item.agreementBuyerSignedAt).trim()) return false;
  return true;
}

/** Phase 11: buyer signed, payment_due, not yet paid (server validates strictly). */
function sellerPhase11EligiblePaymentDueUnpaid(item) {
  if (String(item.dealStatus || '').toLowerCase() !== 'accepted') return false;
  if (item.buyerPaymentCompleted) return false;
  return String(item.buyerPostSaleStage || '').toLowerCase() === 'payment_due';
}

/** Phase 12: accepted + lease_active + activation timestamp + lease end still on/after today (server validates allocation). */
function agreementEndOnOrAfterToday(iso) {
  if (!iso || String(iso).trim() === '') return false;
  const d = String(iso).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return false;
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return d >= `${y}-${m}-${day}`;
}

function sellerPhase12EligibleTerminateLeaseEarly(item) {
  if (String(item.dealStatus || '').toLowerCase() !== 'accepted') return false;
  if (String(item.buyerPostSaleStage || '').toLowerCase() !== 'lease_active') return false;
  const activated = item.buyerLeaseActivatedAt != null && String(item.buyerLeaseActivatedAt).trim();
  if (!activated) return false;
  return agreementEndOnOrAfterToday(item.agreementEndDate);
}

function readStoredLayout() {
  try {
    const v = localStorage.getItem(LAYOUT_KEY);
    if (v === 'table' || v === 'cards') return v;
  } catch {
    /* ignore */
  }
  return 'cards';
}

function formatInrAmount(value) {
  if (value == null || value === '') return '—';
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''));
  if (!Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return String(value);
  }
}

const SELLER_COMMERCIAL_DOC_TYPES = new Set([
  'seller_payout_advice',
  'platform_commission_invoice',
  'termination_settlement_statement',
  'refund_note',
  'credit_note',
  'debit_note',
  'payment_receipt',
]);

function formatCommercialDocTypeLabel(t) {
  const key = String(t || '').toLowerCase();
  const labels = {
    buyer_tax_invoice: 'Buyer tax invoice',
    seller_payout_advice: 'Seller payout advice',
    platform_commission_invoice: 'Platform commission invoice',
    payment_receipt: 'Payment receipt',
    refund_note: 'Refund note',
    credit_note: 'Credit note',
    debit_note: 'Debit note',
    termination_settlement_statement: 'Termination settlement statement',
  };
  return labels[key] || key.replace(/_/g, ' ') || '—';
}

function formatCommercialDocStatus(status) {
  const s = String(status || '').toLowerCase();
  return s ? s.replace(/_/g, ' ') : '—';
}

function formatCommercialDocCurrencyAmount(value, currency) {
  if (value == null || value === '') return '—';
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''));
  if (!Number.isFinite(n)) return '—';
  const cur = (currency && String(currency).trim()) || 'INR';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur, maximumFractionDigits: 2 }).format(n);
  } catch {
    return `${cur} ${n.toFixed(2)}`;
  }
}

function sellerSettlementTitle(tf) {
  if (!tf) return 'Termination settlement';
  if (tf.terminationType === 'mutual') return 'Lease terminated by mutual agreement';
  if (tf.terminatedByRole === 'buyer') return 'Early termination by buyer';
  return 'Termination by seller';
}

function sellerSettlementStatusLabel(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'calculated') return 'Pending settlement';
  return s ? s.replace(/_/g, ' ') : '—';
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function truncateMessage(s, max = 120) {
  if (s == null || String(s).trim() === '') return '—';
  const t = String(s).trim();
  return t.length <= max ? t : `${t.slice(0, max)}…`;
}

function urgencyLabel(u) {
  return formatInfrastructureLeasingUrgency(u);
}

/** Backend sends camelCase; tolerate snake_case if any proxy transforms keys. */
function buyerRequirementEnquiryId(item) {
  const v = item?.buyerRequirementId ?? item?.buyer_requirement_id;
  return v != null && v !== '' && Number.isFinite(Number(v)) && Number(v) >= 1 ? Math.trunc(Number(v)) : null;
}

function dealBadgeClass(dealStatus, naturalLeaseCompleted) {
  if (naturalLeaseCompleted) return 'text-bg-info text-dark';
  const d = String(dealStatus || '').toLowerCase();
  if (d === 'accepted') return 'text-bg-success';
  if (d === 'completed') return 'text-bg-info text-dark';
  if (d === 'rejected') return 'text-bg-danger';
  if (d === 'withdrawn') return 'text-bg-secondary';
  if (d === 'pending_signature') return 'text-bg-warning text-dark';
  return 'text-bg-secondary';
}

function RatingStars({ rating }) {
  const r = Math.min(5, Math.max(0, Math.round(Number(rating) || 0)));
  return (
    <span className="text-warning text-nowrap" role="img" aria-label={`${r} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <FaStar
          key={i}
          className={i <= r ? '' : 'text-secondary opacity-25'}
          style={{ fontSize: '0.8rem' }}
          aria-hidden
        />
      ))}
    </span>
  );
}

/** Prefer title, else truncated comment — same idea as buyer My enquiries. */
function buyerReviewSummaryLine(rev) {
  if (!rev) return '';
  const t = rev.title != null && String(rev.title).trim();
  if (t) return String(rev.title).trim();
  const c = rev.comment != null && String(rev.comment).trim();
  if (c) return truncateMessage(c, 88);
  return '';
}

/**
 * Buyer → seller review: compact summary + expandable full comment (matches buyer enquiries page).
 */
function BuyerReviewCollapsible({ enquiryId, buyerReview, expanded, onToggle, compact = false }) {
  const hasReview = Boolean(buyerReview);
  const comment =
    buyerReview?.comment != null && String(buyerReview.comment).trim()
      ? String(buyerReview.comment).trim()
      : '';
  const summary = hasReview ? buyerReviewSummaryLine(buyerReview) : '';
  const canExpand = Boolean(comment);

  if (!hasReview) {
    return null;
  }

  const bodyId = `ile-seller-review-desc-${enquiryId}`;
  const toggleId = `ile-seller-review-toggle-${enquiryId}`;

  const inner = (
    <div className={compact ? '' : 'ile-seller-enquiry-review-box border rounded px-2 py-2 bg-light'}>
      <div className="d-flex align-items-start gap-2">
        <div className="flex-shrink-0 pt-0">
          <RatingStars rating={buyerReview.rating} />
        </div>
        <div className="flex-grow-1 min-w-0">
          <div className="fw-semibold text-body small">{summary || 'Review'}</div>
          {buyerReview.reviewedAt ? (
            <div className="small text-muted">{formatDate(buyerReview.reviewedAt)}</div>
          ) : null}
          {canExpand ? (
            <button
              type="button"
              id={toggleId}
              className="btn btn-link btn-sm p-0 ile-seller-review-toggle mt-1"
              onClick={() => onToggle(enquiryId)}
              aria-expanded={expanded}
              aria-controls={bodyId}
            >
              <span className="small d-inline-flex align-items-center gap-1">
                {expanded ? (
                  <>
                    Hide details
                    <FaChevronUp aria-hidden size={11} />
                  </>
                ) : (
                  <>
                    Show full review
                    <FaChevronDown aria-hidden size={11} />
                  </>
                )}
              </span>
            </button>
          ) : null}
        </div>
      </div>
      {canExpand ? (
        <div
          className={`ile-seller-review-expandable${expanded ? ' is-open' : ''}`}
          id={bodyId}
          role="region"
          aria-labelledby={toggleId}
        >
          <div className="ile-seller-review-expandable-inner">
            <p className="small text-secondary mb-0 mt-2 pt-2 border-top ile-seller-review-full-text">{comment}</p>
          </div>
        </div>
      ) : null}
    </div>
  );

  if (compact) {
    return inner;
  }

  return <div className="mt-3 pt-3 border-top ile-seller-enquiry-buyer-review">{inner}</div>;
}

export default function InfrastructureLeasingSellerListingEnquiriesPage() {
  const navigate = useNavigate();
  const { listingId: listingIdParam } = useParams();
  const listingIdNum = useMemo(() => {
    const n = parseInt(String(listingIdParam), 10);
    return Number.isFinite(n) && n >= 1 ? n : NaN;
  }, [listingIdParam]);

  const [page, setPage] = useState(1);
  const pageSize = 12;
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [listingTitle, setListingTitle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [viewMode, setViewMode] = useState(readStoredLayout);
  const [acceptTarget, setAcceptTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  /** Popup step 1: pick Accept vs Reject before opening the detail modal */
  const [respondChoiceTarget, setRespondChoiceTarget] = useState(null);
  const [rejectionDetailItem, setRejectionDetailItem] = useState(null);
  const [quotationModalItem, setQuotationModalItem] = useState(null);
  const [expandedBuyerReviews, setExpandedBuyerReviews] = useState(() => new Set());
  const [commercialDocsModalItem, setCommercialDocsModalItem] = useState(null);
  const [commercialDocsModalRows, setCommercialDocsModalRows] = useState([]);
  const [commercialDocsModalLoading, setCommercialDocsModalLoading] = useState(false);
  const [commercialDocModalDownloadId, setCommercialDocModalDownloadId] = useState(null);

  const toggleBuyerReviewExpand = useCallback((enquiryId) => {
    const id = Math.trunc(Number(enquiryId));
    if (!Number.isFinite(id)) return;
    setExpandedBuyerReviews((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(LAYOUT_KEY, viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  useEffect(() => {
    setPage(1);
  }, [listingIdNum]);

  const load = useCallback(async () => {
    if (!Number.isFinite(listingIdNum)) {
      setLoading(false);
      setLoadError('invalid_id');
      setItems([]);
      setTotal(0);
      setTotalPages(0);
      setListingTitle(null);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetchSellerListingEnquiries(listingIdNum, { page, pageSize });
      if (res?.success && res.data) {
        const d = res.data;
        setItems(Array.isArray(d.items) ? d.items : []);
        setTotal(Number(d.total) || 0);
        setTotalPages(Number(d.totalPages) || 0);
        setListingTitle(d.listingTitle != null && String(d.listingTitle).trim() ? String(d.listingTitle).trim() : null);
      } else {
        setItems([]);
        setTotal(0);
        setTotalPages(0);
        setListingTitle(null);
      }
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.message || e?.message;
      if (status === 403) {
        setLoadError('forbidden');
      } else if (status === 404) {
        setLoadError('not_found');
      } else {
        setLoadError('error');
        toast.error(msg || 'Could not load enquiries.');
      }
      setItems([]);
      setTotal(0);
      setTotalPages(0);
      setListingTitle(null);
    } finally {
      setLoading(false);
    }
  }, [listingIdNum, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!commercialDocsModalItem || !Number.isFinite(listingIdNum)) {
      setCommercialDocsModalRows([]);
      setCommercialDocsModalLoading(false);
      return undefined;
    }
    const eid = Math.trunc(Number(commercialDocsModalItem.id));
    if (!Number.isFinite(eid) || eid < 1) {
      setCommercialDocsModalRows([]);
      return undefined;
    }
    let cancelled = false;
    setCommercialDocsModalLoading(true);
    (async () => {
      try {
        const res = await getSellerCommercialDocuments(listingIdNum, eid);
        if (cancelled) return;
        const raw = res?.success && Array.isArray(res.data?.items) ? res.data.items : [];
        setCommercialDocsModalRows(
          raw.filter((d) => SELLER_COMMERCIAL_DOC_TYPES.has(String(d.documentType || '').toLowerCase()))
        );
      } catch {
        if (!cancelled) setCommercialDocsModalRows([]);
      } finally {
        if (!cancelled) setCommercialDocsModalLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [commercialDocsModalItem, listingIdNum]);

  const handleDownloadSellerCommercialDoc = useCallback(
    async (doc) => {
      const id = doc?.id != null ? Math.trunc(Number(doc.id)) : NaN;
      if (!Number.isFinite(listingIdNum) || listingIdNum < 1 || !Number.isFinite(id) || id < 1) return;
      const eid = commercialDocsModalItem?.id != null ? Math.trunc(Number(commercialDocsModalItem.id)) : NaN;
      if (!Number.isFinite(eid) || eid < 1) return;
      const safeName =
        doc.documentNumber && String(doc.documentNumber).trim()
          ? `${String(doc.documentNumber).trim().replace(/[^\w.-]+/g, '_')}.pdf`
          : `commercial-document-${id}.pdf`;
      setCommercialDocModalDownloadId(id);
      try {
        await downloadSellerCommercialDocument(listingIdNum, eid, id, safeName);
      } catch (e) {
        toast.error(e?.message || e?.response?.data?.message || 'PDF download failed. Please retry.');
      } finally {
        setCommercialDocModalDownloadId(null);
      }
    },
    [listingIdNum, commercialDocsModalItem]
  );

  const handleUnwindPendingDeal = useCallback(
    async (item) => {
      if (
        !window.confirm(
          'Cancel this pending deal? Reserved inventory will be released and the agreement will be marked cancelled.'
        )
      ) {
        return;
      }
      try {
        const res = await postUnwindPendingSignatureDeal(listingIdNum, item.id, {});
        if (res?.success === false) {
          toast.error(res?.message || 'Could not cancel pending deal');
          return;
        }
        toast.success(res?.message || 'Pending deal cancelled');
        load();
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || 'Could not cancel pending deal');
      }
    },
    [listingIdNum, load]
  );

  const handleCancelAcceptedBeforeBuyerSign = useCallback(
    async (item) => {
      if (
        !window.confirm(
          'Cancel this accepted deal before the buyer signs? Reserved inventory will be released, the agreement will be cancelled, and the enquiry will be withdrawn. This is not a payment refund or lease termination.'
        )
      ) {
        return;
      }
      try {
        const res = await postSellerCancelAcceptedInfrastructureDeal(listingIdNum, item.id, {});
        if (res?.success === false) {
          toast.error(res?.message || 'Could not cancel deal');
          return;
        }
        toast.success(res?.message || 'Accepted deal cancelled');
        load();
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || 'Could not cancel deal');
      }
    },
    [listingIdNum, load]
  );

  const handleCancelPaymentDueUnpaid = useCallback(
    async (item) => {
      if (
        !window.confirm(
          'Cancel this unpaid deal? The buyer has signed but payment has not been completed. Reserved inventory will be released, the agreement will be cancelled, and the enquiry will be withdrawn. This is not a refund or invoice reversal.'
        )
      ) {
        return;
      }
      try {
        const res = await postSellerCancelPaymentDueUnpaidDeal(listingIdNum, item.id, {});
        if (res?.success === false) {
          toast.error(res?.message || 'Could not cancel deal');
          return;
        }
        toast.success(res?.message || 'Unpaid deal cancelled');
        load();
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || 'Could not cancel deal');
      }
    },
    [listingIdNum, load]
  );

  const handleTerminateLeaseEarly = useCallback(
    async (item) => {
      if (
        !window.confirm(
          'Terminate this lease early? Confirmed inventory will be cancelled, the agreement will end, and the enquiry will be withdrawn. This does not reverse payments or invoices.'
        )
      ) {
        return;
      }
      try {
        const res = await postSellerTerminateLeaseEarly(listingIdNum, item.id, {});
        if (res?.success === false) {
          toast.error(res?.message || 'Could not terminate lease');
          return;
        }
        toast.success(res?.message || 'Lease terminated early');
        load();
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || 'Could not terminate lease');
      }
    },
    [listingIdNum, load]
  );

  const handleRequestMutualTermination = useCallback(
    async (item) => {
      if (
        !window.confirm(
          'Propose ending this lease by mutual agreement? The buyer must confirm before the lease ends on the platform (same outcome as early termination once confirmed).'
        )
      ) {
        return;
      }
      try {
        const res = await postSellerRequestMutualLeaseTermination(listingIdNum, item.id, {});
        if (res?.success === false) {
          toast.error(res?.message || 'Could not propose mutual termination');
          return;
        }
        toast.success(res?.message || 'Mutual termination proposed');
        load();
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || 'Could not propose mutual termination');
      }
    },
    [listingIdNum, load]
  );

  const handleConfirmMutualTermination = useCallback(
    async (item) => {
      if (
        !window.confirm(
          'Confirm mutual lease termination? This runs the same early-termination steps as a unilateral exit (allocation released, agreement cancelled, enquiry withdrawn).'
        )
      ) {
        return;
      }
      try {
        const res = await postSellerConfirmMutualLeaseTermination(listingIdNum, item.id, {});
        if (res?.success === false) {
          toast.error(res?.message || 'Could not confirm mutual termination');
          return;
        }
        toast.success(res?.message || 'Mutual termination confirmed');
        load();
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || 'Could not confirm mutual termination');
      }
    },
    [listingIdNum, load]
  );

  const handleWithdrawMutualTermination = useCallback(
    async (item) => {
      if (!window.confirm('Withdraw your mutual termination request? The lease stays active until ended another way.')) {
        return;
      }
      try {
        const res = await postSellerWithdrawMutualLeaseTermination(listingIdNum, item.id, {});
        if (res?.success === false) {
          toast.error(res?.message || 'Could not withdraw request');
          return;
        }
        toast.success(res?.message || 'Request withdrawn');
        load();
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || 'Could not withdraw request');
      }
    },
    [listingIdNum, load]
  );

  const handleRejectMutualTermination = useCallback(
    async (item) => {
      if (!window.confirm('Reject the buyer’s mutual termination proposal? The lease stays active.')) {
        return;
      }
      const reasonRaw = window.prompt('Optional reason (max ~500 chars, leave blank to skip):', '') || '';
      try {
        const res = await postSellerRejectMutualLeaseTermination(listingIdNum, item.id, {
          reason: reasonRaw.trim() || undefined,
        });
        if (res?.success === false) {
          toast.error(res?.message || 'Could not reject request');
          return;
        }
        toast.success(res?.message || 'Request rejected');
        load();
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || 'Could not reject request');
      }
    },
    [listingIdNum, load]
  );

  const rangeLabel = useMemo(() => {
    if (total === 0) return '0 results';
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);
    return `Showing ${start}–${end} of ${total}`;
  }, [total, page, pageSize]);

  const goPage = (next) => {
    if (next < 1 || (totalPages > 0 && next > totalPages)) return;
    setPage(next);
  };

  const sellerPaymentInvoicePath = (item) =>
    `/infrastructure-leasing/my-properties/${listingIdNum}/enquiries/${item.id}/payment-invoice`;

  const renderSellerCommercialDocumentsPanel = (item) => {
    const cds = item.commercialDocumentSummary || {};
    return (
      <div className="small border rounded p-2 bg-white mb-2 text-start">
        <div className="fw-semibold mb-1">Commercial documents</div>
        <p className="text-muted small mb-2">
          Commercial documents are generated from payment, settlement and tax records. GST/e-invoice details are subject to
          finance approval where applicable.
        </p>
        <div className="text-muted mb-2 mt-2">
          {cds.totalDocuments != null ? `${cds.totalDocuments} on file` : '—'}
          {cds.pendingApprovalCount ? ` · ${cds.pendingApprovalCount} pending approval` : ''}
          {cds.finalisedCount != null && cds.finalisedCount > 0 ? ` · ${cds.finalisedCount} finalised` : ''}
          {cds.downloadableCount != null && cds.downloadableCount > 0 ? ` · ${cds.downloadableCount} PDF(s)` : ''}
          {cds.hasTaxReviewPending ? (
            <span className="badge bg-warning text-dark ms-1">Tax review</span>
          ) : null}
        </div>
        <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => setCommercialDocsModalItem(item)}>
          View &amp; download
        </button>
      </div>
    );
  };

  const renderSellerCommercialDocsTableLink = (item) => {
    const cds = item.commercialDocumentSummary || {};
    return (
      <div className="mt-1">
        <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => setCommercialDocsModalItem(item)}>
          Commercial documents
          {cds.totalDocuments != null ? ` (${cds.totalDocuments})` : ''}
        </button>
        {cds.hasTaxReviewPending ? (
          <span className="badge bg-warning text-dark ms-1 small">Tax review</span>
        ) : null}
      </div>
    );
  };

  const renderSellerLedgerSummary = (item) => {
    const ps = item.paymentSettlementSummary || {};
    const rc = String(ps.reconciliationStatus || 'no_payment').replace(/_/g, ' ');
    return (
      <div className="small border rounded p-2 bg-light mb-2 text-start">
        <div className="fw-semibold mb-1">Settlement summary (ledger)</div>
        <div className="text-muted">
          Buyer payment: <span className="text-body">{ps.paymentStatus ? String(ps.paymentStatus) : '—'}</span>
        </div>
        <div>
          Seller receivable (open): {formatInrAmount(ps.sellerReceivableOpen)} · Platform commission (open):{' '}
          {formatInrAmount(ps.platformCommissionOpen)}
        </div>
        <div>
          Refund due: {formatInrAmount(ps.refundDue)} · Penalty due: {formatInrAmount(ps.penaltyDue)}
        </div>
        <div className="text-muted mt-1">
          Payout: {ps.payoutStatus || '—'} · Refund queue: {ps.refundStatus || '—'}
        </div>
        <div className="mt-1">
          <span className="badge bg-secondary">{rc}</span>
        </div>
        <p className="text-muted mt-2 mb-0 small">
          Payments run through authorised partners only — not escrow or stored-value wallets. Ledger is for visibility.
        </p>
      </div>
    );
  };

  const renderDealActions = (item, layout = 'cards') => {
    const ds = String(item.dealStatus || '').toLowerCase();
    const stackClass =
      layout === 'table'
        ? 'd-inline-flex flex-column gap-2 align-items-end text-end'
        : 'd-flex flex-column gap-2 align-items-stretch';
    const buyerReqId = buyerRequirementEnquiryId(item);
    const isBuyerRequirementEnquiry = buyerReqId != null;

    if (ds === 'submitted' && isBuyerRequirementEnquiry) {
      const reqPath = `/infrastructure-leasing/my-properties/${listingIdNum}/enquiries/${item.id}/buyer-requirement`;
      const hasQuote = Boolean(item.sellerRequirementQuotation?.quotedPrice);
      return (
        <div className={stackClass}>
          <Link to={reqPath} className="btn btn-sm btn-outline-dark text-nowrap">
            View requirement
          </Link>
          <button type="button" className="btn btn-sm btn-dark text-nowrap" onClick={() => setQuotationModalItem(item)}>
            {hasQuote ? 'Update quotation' : 'Submit quotation'}
          </button>
        </div>
      );
    }

    if (ds === 'submitted') {
      return (
        <button type="button" className="btn btn-sm btn-dark" onClick={() => setRespondChoiceTarget(item)}>
          Accept / reject
        </button>
      );
    }
    if (ds === 'pending_signature') {
      return (
        <div className={stackClass}>
          <Link
            to={`/infrastructure-leasing/my-properties/${listingIdNum}/enquiries/${item.id}/agreement`}
            className="btn btn-sm btn-dark"
          >
            Sign agreement
          </Link>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger text-nowrap"
            onClick={() => handleUnwindPendingDeal(item)}
          >
            Cancel pending deal
          </button>
          {item.buyerPaymentCompleted ? (
            <Link to={sellerPaymentInvoicePath(item)} className="btn btn-sm btn-primary text-nowrap">
              View Payment Details / Invoice
            </Link>
          ) : null}
        </div>
      );
    }
    if (ds === 'accepted') {
      return (
        <div className={stackClass}>
          <span className={item.naturalLeaseCompleted ? 'small text-muted' : 'small text-success'}>
            {item.naturalLeaseCompleted ? 'Lease completed' : 'Deal accepted'}
          </span>
          <Link
            to={`/infrastructure-leasing/my-properties/${listingIdNum}/enquiries/${item.id}/agreement`}
            className="btn btn-sm btn-outline-dark text-nowrap"
          >
            View agreement
          </Link>
          {sellerPhase10EligibleAcceptedPreBuyer(item) ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger text-nowrap"
              onClick={() => handleCancelAcceptedBeforeBuyerSign(item)}
            >
              Cancel before buyer sign
            </button>
          ) : null}
          {sellerPhase11EligiblePaymentDueUnpaid(item) ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger text-nowrap"
              onClick={() => handleCancelPaymentDueUnpaid(item)}
            >
              Cancel unpaid deal
            </button>
          ) : null}
          {sellerPhase12EligibleTerminateLeaseEarly(item) ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger text-nowrap"
              onClick={() => handleTerminateLeaseEarly(item)}
            >
              Terminate lease early
            </button>
          ) : null}
          {item.mutualLeaseTerminationStatus === 'pending' && item.mutualLeaseTerminationExpiresAt ? (
            <span className="small text-muted">
              Mutual proposal expires: {formatDate(item.mutualLeaseTerminationExpiresAt)}
            </span>
          ) : null}
          {item.mutualLeaseTerminationStatus === 'rejected' ? (
            <span className="small text-danger">
              Mutual request rejected
              {item.mutualLeaseTerminationRejectionReason
                ? `: ${String(item.mutualLeaseTerminationRejectionReason).slice(0, 200)}`
                : ''}
            </span>
          ) : null}
          {item.mutualLeaseTerminationStatus === 'withdrawn' ? (
            <span className="small text-muted">Mutual request withdrawn</span>
          ) : null}
          {item.mutualLeaseTerminationStatus === 'expired' ? (
            <span className="small text-muted">Mutual request expired</span>
          ) : null}
          {Array.isArray(item.mutualLeaseTerminationTimeline) && item.mutualLeaseTerminationTimeline.length > 0 ? (
            <ul className="small text-muted mb-0 ps-3">
              {item.mutualLeaseTerminationTimeline.map((s) => (
                <li key={s.key}>{s.label}</li>
              ))}
            </ul>
          ) : null}
          {item.mutualLeaseTerminationCanRequest ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary text-nowrap"
              onClick={() => handleRequestMutualTermination(item)}
            >
              Propose mutual termination
            </button>
          ) : null}
          {item.mutualLeaseTerminationCanWithdraw ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary text-nowrap"
              onClick={() => handleWithdrawMutualTermination(item)}
            >
              Withdraw request
            </button>
          ) : null}
          {item.mutualLeaseTerminationCanConfirm ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger text-nowrap"
              onClick={() => handleConfirmMutualTermination(item)}
            >
              Confirm mutual termination
            </button>
          ) : null}
          {item.mutualLeaseTerminationCanReject ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-dark text-nowrap"
              onClick={() => handleRejectMutualTermination(item)}
            >
              Reject proposal
            </button>
          ) : null}
          {item.buyerPaymentCompleted ? (
            <Link to={sellerPaymentInvoicePath(item)} className="btn btn-sm btn-primary text-nowrap">
              View Payment Details / Invoice
            </Link>
          ) : null}
        </div>
      );
    }
    if (ds === 'completed') {
      return (
        <div className={stackClass}>
          <span className="small text-muted">Lease completed</span>
          <Link
            to={`/infrastructure-leasing/my-properties/${listingIdNum}/enquiries/${item.id}/agreement`}
            className="btn btn-sm btn-outline-dark text-nowrap"
          >
            View agreement
          </Link>
          {item.buyerPaymentCompleted ? (
            <Link to={sellerPaymentInvoicePath(item)} className="btn btn-sm btn-primary text-nowrap">
              View Payment Details / Invoice
            </Link>
          ) : null}
        </div>
      );
    }
    if (ds === 'rejected') {
      return (
        <button
          type="button"
          className="btn btn-sm btn-outline-danger text-nowrap"
          onClick={() => setRejectionDetailItem(item)}
        >
          Rejected details
        </button>
      );
    }
    if (ds === 'withdrawn') {
      const tf = item.terminationFinancials;
      const payableNum =
        tf?.payable != null && String(tf.payable).trim() !== ''
          ? parseFloat(String(tf.payable).replace(/,/g, ''))
          : NaN;
      return (
        <div className={stackClass}>
          {tf ? (
            <div className="small border rounded p-2 bg-light text-start w-100">
              <div className="fw-semibold mb-1">{sellerSettlementTitle(tf)}</div>
              <div className="text-muted">
                Utilized: {formatInrAmount(tf.utilizedValue)} · Unused: {formatInrAmount(tf.unusedValue)}
              </div>
              {tf.terminationType === 'mutual' ? (
                <div className="mt-1">Refund: {formatInrAmount(tf.refund)}</div>
              ) : null}
              {tf.terminationType === 'unilateral' && tf.terminatedByRole === 'buyer' ? (
                <div className="mt-1">
                  Penalty: {formatInrAmount(tf.penalty)} · Refund: {formatInrAmount(tf.refund)}
                </div>
              ) : null}
              {tf.terminationType === 'unilateral' && tf.terminatedByRole === 'seller' ? (
                <div className="mt-1">
                  Compensation: {formatInrAmount(tf.penalty)} · Total refund: {formatInrAmount(tf.refund)}
                </div>
              ) : null}
              {Number.isFinite(payableNum) && payableNum > 0 ? (
                <div className="mt-1">Payable: {formatInrAmount(tf.payable)}</div>
              ) : null}
              <div className="mt-1 text-muted">
                Status: <span className="text-body">{sellerSettlementStatusLabel(tf.settlementStatus)}</span>
              </div>
            </div>
          ) : null}
          <span className="small text-muted">Deal withdrawn</span>
          <Link
            to={`/infrastructure-leasing/my-properties/${listingIdNum}/enquiries/${item.id}/agreement`}
            className="btn btn-sm btn-outline-dark text-nowrap"
          >
            View agreement
          </Link>
        </div>
      );
    }
    return null;
  };

  const headingSubtitle = listingTitle
    ? `${listingTitle} · Listing #${listingIdNum}`
    : `Listing #${listingIdNum}`;

  if (!Number.isFinite(listingIdNum)) {
    return (
      <div className="container-fluid ile-seller-listing-enquiries py-4">
        <div className="border rounded p-4 bg-white">
          <p className="mb-3">Invalid listing.</p>
          <Link to="/infrastructure-leasing/my-properties" className="btn btn-dark">
            Back to My properties / assets
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid ile-seller-listing-enquiries py-4">
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item">
            <Link to="/dashboard/seller">Seller dashboard</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/infrastructure-leasing/my-properties">My properties / assets</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Enquiries
          </li>
        </ol>
      </nav>

      <div className="mb-3 d-flex flex-wrap justify-content-between align-items-start gap-3">
        <div>
          <h1 className="h3 mb-1">Enquiries received</h1>
          <p className="text-muted mb-0 small">{headingSubtitle}</p>
        </div>
        <Link
          to={`/infrastructure-leasing/listing/${listingIdNum}`}
          className="btn btn-outline-dark btn-sm d-inline-flex align-items-center gap-2"
        >
          View public listing <FaExternalLinkAlt size={12} aria-hidden />
        </Link>
      </div>

      {loadError === 'error' && (
        <div className="border rounded p-4 bg-white mb-3">
          <p className="text-muted mb-3">Something went wrong loading enquiries.</p>
          <Link to="/infrastructure-leasing/my-properties" className="btn btn-dark">
            Back to My properties / assets
          </Link>
        </div>
      )}

      {loadError === 'forbidden' && (
        <div className="border rounded p-4 bg-white mb-3">
          <p className="text-muted mb-3">You don’t have access to enquiries for this listing.</p>
          <Link to="/infrastructure-leasing/my-properties" className="btn btn-dark">
            Back to My properties / assets
          </Link>
        </div>
      )}

      {loadError === 'not_found' && (
        <div className="border rounded p-4 bg-white mb-3">
          <p className="mb-3 text-muted mb-0">This listing was not found or is not published.</p>
          <Link to="/infrastructure-leasing/my-properties" className="btn btn-dark">
            Back to My properties / assets
          </Link>
        </div>
      )}

      {loadError !== 'forbidden' && loadError !== 'not_found' && loadError !== 'error' && (
        <>
          {loading ? (
            <div className="text-muted py-5">Loading enquiries…</div>
          ) : items.length === 0 ? (
            <div className="border rounded p-5 text-center bg-white ile-seller-listing-enquiries-empty">
              <p className="mb-3 text-muted">No enquiries yet for this listing.</p>
              <Link to="/infrastructure-leasing/my-properties" className="btn btn-outline-dark">
                Back to My properties / assets
              </Link>
            </div>
          ) : (
            <>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3 ile-seller-listing-enquiries-toolbar">
                <span className="small text-muted">{rangeLabel}</span>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="small text-muted d-none d-sm-inline">Layout</span>
                  <div className="ile-search-view-toggles" role="group" aria-label="Choose layout">
                    <button
                      type="button"
                      className={viewMode === 'cards' ? 'active' : ''}
                      onClick={() => setViewMode('cards')}
                      title="Cards"
                      aria-pressed={viewMode === 'cards'}
                      aria-label="Card layout"
                    >
                      <FaTh aria-hidden />
                    </button>
                    <button
                      type="button"
                      className={viewMode === 'table' ? 'active' : ''}
                      onClick={() => setViewMode('table')}
                      title="Table"
                      aria-pressed={viewMode === 'table'}
                      aria-label="Table layout"
                    >
                      <FaTable aria-hidden />
                    </button>
                  </div>
                </div>
              </div>

              {viewMode === 'cards' ? (
                <div className="row g-3">
                  {items.map((item) => (
                    <div key={item.id} className="col-12 col-md-6 col-xl-4 d-flex">
                      <div className="card ile-seller-listing-enquiries-card h-100 w-100 border shadow-sm">
                        <div className="card-body d-flex flex-column">
                          <div className="d-flex justify-content-between align-items-start gap-2 mb-2 flex-wrap">
                            <span className={`badge ${dealBadgeClass(item.dealStatus, item.naturalLeaseCompleted)}`}>
                              {item.displayStatusLabel ?? item.statusLabel}
                            </span>
                            <span className="d-flex flex-wrap gap-1 justify-content-end">
                              {buyerRequirementEnquiryId(item) != null ? (
                                <span className="badge bg-info text-dark">Buyer requirement</span>
                              ) : null}
                              <span className="badge bg-light text-dark border">{urgencyLabel(item.urgency)}</span>
                            </span>
                          </div>
                          <h2 className="h6 card-title mb-2">{item.buyerName}</h2>
                          <p className="small text-muted mb-2">
                            <FaEnvelope className="me-1" aria-hidden />
                            {item.contactEmail ? (
                              <a href={`mailto:${encodeURIComponent(item.contactEmail)}`}>{item.contactEmail}</a>
                            ) : (
                              '—'
                            )}
                          </p>
                          <p className="small text-muted mb-2">
                            {item.contactPhone ? (
                              <a href={`tel:${String(item.contactPhone).replace(/\s/g, '')}`}>{item.contactPhone}</a>
                            ) : (
                              '—'
                            )}
                          </p>
                          {item.contactCompany ? (
                            <p className="small mb-2">
                              <strong>Company:</strong> {item.contactCompany}
                            </p>
                          ) : null}
                          <div className="small mb-3 flex-grow-1">
                            <strong className="d-block mb-1">Message</strong>
                            {item.message && String(item.message).trim() ? (
                              <HomeClampedText text={String(item.message)} className="mb-0 text-secondary" />
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </div>
                          {item.buyerReview ? (
                            <BuyerReviewCollapsible
                              enquiryId={item.id}
                              buyerReview={item.buyerReview}
                              expanded={expandedBuyerReviews.has(Math.trunc(Number(item.id)))}
                              onToggle={toggleBuyerReviewExpand}
                            />
                          ) : null}
                          <div className="small text-muted mb-3">Received {formatDate(item.createdAt)}</div>
                          {renderSellerCommercialDocumentsPanel(item)}
                          {renderSellerLedgerSummary(item)}
                          <div className="mt-auto pt-2 border-top">{renderDealActions(item, 'cards')}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="table-responsive border rounded bg-white ile-seller-listing-enquiries-table-wrap">
                  <table className="table table-hover align-middle mb-0 ile-seller-listing-enquiries-table">
                    <thead className="table-light">
                      <tr>
                        <th className="text-muted small text-uppercase">Buyer</th>
                        <th className="text-muted small text-uppercase">Contact</th>
                        <th className="text-muted small text-uppercase">Message</th>
                        <th className="text-muted small text-uppercase">Date</th>
                        <th className="text-muted small text-uppercase">Status</th>
                        <th className="text-muted small text-uppercase">Priority</th>
                        <th className="text-muted small text-uppercase">
                          <span className="visually-hidden">Review</span>
                        </th>
                        <th className="text-end text-muted small text-uppercase">Deal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr key={item.id}>
                          <td className="fw-medium">
                            <div>{item.buyerName}</div>
                            {item.contactCompany ? (
                              <div className="small text-muted">{item.contactCompany}</div>
                            ) : null}
                            {renderSellerCommercialDocsTableLink(item)}
                          </td>
                          <td className="small">
                            <div>
                              {item.contactEmail ? (
                                <a href={`mailto:${encodeURIComponent(item.contactEmail)}`}>{item.contactEmail}</a>
                              ) : (
                                '—'
                              )}
                            </div>
                            <div className="text-muted">
                              {item.contactPhone ? (
                                <a href={`tel:${String(item.contactPhone).replace(/\s/g, '')}`}>
                                  {item.contactPhone}
                                </a>
                              ) : (
                                '—'
                              )}
                            </div>
                          </td>
                          <td className="small" style={{ maxWidth: 280 }}>
                            <span title={item.message}>{truncateMessage(item.message, 140)}</span>
                          </td>
                          <td className="small text-nowrap">{formatDate(item.createdAt)}</td>
                          <td>
                            <div className="d-flex flex-column gap-1 align-items-start">
                              <span className={`badge ${dealBadgeClass(item.dealStatus, item.naturalLeaseCompleted)}`}>
                              {item.displayStatusLabel ?? item.statusLabel}
                            </span>
                              {buyerRequirementEnquiryId(item) != null ? (
                                <span className="badge bg-info text-dark">Buyer requirement</span>
                              ) : null}
                            </div>
                          </td>
                          <td className="small">{urgencyLabel(item.urgency)}</td>
                          <td className="small ile-seller-enquiries-table-review-cell" style={{ minWidth: 160 }}>
                            {item.buyerReview ? (
                              <BuyerReviewCollapsible
                                enquiryId={item.id}
                                buyerReview={item.buyerReview}
                                expanded={expandedBuyerReviews.has(Math.trunc(Number(item.id)))}
                                onToggle={toggleBuyerReviewExpand}
                                compact
                              />
                            ) : null}
                          </td>
                          <td className="text-end small">{renderDealActions(item, 'table')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {totalPages > 1 && (
                <nav
                  className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-4"
                  aria-label="Enquiry pages"
                >
                  <span className="small text-muted">{rangeLabel}</span>
                  <div className="btn-group">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      disabled={page <= 1}
                      onClick={() => goPage(page - 1)}
                      aria-label="Previous page"
                    >
                      <FaChevronLeft aria-hidden />
                    </button>
                    <span className="btn btn-sm btn-outline-secondary disabled text-dark">
                      Page {page} / {totalPages}
                    </span>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      disabled={page >= totalPages}
                      onClick={() => goPage(page + 1)}
                      aria-label="Next page"
                    >
                      <FaChevronRight aria-hidden />
                    </button>
                  </div>
                </nav>
              )}
            </>
          )}
        </>
      )}

      {commercialDocsModalItem ? (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ile-commercial-docs-modal-title"
          onClick={(ev) => {
            if (ev.target === ev.currentTarget) setCommercialDocsModalItem(null);
          }}
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h2 id="ile-commercial-docs-modal-title" className="modal-title h5 mb-0">
                  Commercial documents
                  {commercialDocsModalItem.buyerName ? (
                    <span className="fw-normal text-muted d-block small mt-1">{commercialDocsModalItem.buyerName}</span>
                  ) : null}
                </h2>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setCommercialDocsModalItem(null)}
                />
              </div>
              <div className="modal-body small">
                <p className="text-muted">
                  Commercial documents are generated from payment, settlement and tax records. GST/e-invoice details are
                  subject to finance approval where applicable.
                </p>
                {commercialDocsModalLoading ? (
                  <p className="text-muted mb-0">Loading documents…</p>
                ) : commercialDocsModalRows.length === 0 ? (
                  <p className="text-muted mb-0">No seller-visible commercial documents yet for this enquiry.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th scope="col">Document type</th>
                          <th scope="col">Document number</th>
                          <th scope="col">Status</th>
                          <th scope="col" className="text-end">
                            Amount
                          </th>
                          <th scope="col">Country / regime</th>
                          <th scope="col">Generated</th>
                          <th scope="col" className="text-end">
                            Download
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {commercialDocsModalRows.map((row) => {
                          const hasPdf = row.pdfFilePath != null && String(row.pdfFilePath).trim() !== '';
                          const downloading = commercialDocModalDownloadId === row.id;
                          return (
                            <tr key={row.id}>
                              <td>{formatCommercialDocTypeLabel(row.documentType)}</td>
                              <td className="text-break">{row.documentNumber || '—'}</td>
                              <td>{formatCommercialDocStatus(row.documentStatus)}</td>
                              <td className="text-end text-nowrap">
                                {formatCommercialDocCurrencyAmount(row.totalDocumentValue, row.currency)}
                              </td>
                              <td className="small">
                                <div>{row.countryCode || '—'}</div>
                                <div className="text-muted">{row.taxRegimeName || row.taxType || ''}</div>
                              </td>
                              <td className="text-nowrap">{formatDate(row.createdAt)}</td>
                              <td className="text-end">
                                <button
                                  type="button"
                                  className="btn btn-outline-dark btn-sm"
                                  disabled={!hasPdf || downloading}
                                  title={hasPdf ? 'Download PDF' : 'PDF not generated yet'}
                                  onClick={() => void handleDownloadSellerCommercialDoc(row)}
                                >
                                  {downloading ? '…' : 'Download'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <InfrastructureLeasingRespondChoiceModal
        open={Boolean(respondChoiceTarget)}
        onClose={() => setRespondChoiceTarget(null)}
        listingTitle={listingTitle}
        buyerName={respondChoiceTarget?.buyerName}
        onChooseAccept={() => {
          const row = respondChoiceTarget;
          setRespondChoiceTarget(null);
          if (row) setAcceptTarget(row);
        }}
        onChooseReject={() => {
          const row = respondChoiceTarget;
          setRespondChoiceTarget(null);
          if (row) setRejectTarget(row);
        }}
      />
      <InfrastructureLeasingDealAcceptModal
        open={Boolean(acceptTarget)}
        onClose={() => setAcceptTarget(null)}
        listingId={listingIdNum}
        enquiryId={acceptTarget?.id}
        listingTitle={listingTitle}
        buyerName={acceptTarget?.buyerName}
        onSuccess={(data) => {
          const eid = data?.enquiry?.id ?? acceptTarget?.id;
          load();
          setAcceptTarget(null);
          if (eid && Number.isFinite(listingIdNum)) {
            navigate(`/infrastructure-leasing/my-properties/${listingIdNum}/enquiries/${eid}/agreement`);
          }
        }}
      />
      <InfrastructureLeasingDealRejectModal
        open={Boolean(rejectTarget)}
        onClose={() => setRejectTarget(null)}
        listingId={listingIdNum}
        enquiryId={rejectTarget?.id}
        listingTitle={listingTitle}
        buyerName={rejectTarget?.buyerName}
        onSuccess={() => {
          load();
          setRejectTarget(null);
        }}
      />
      <InfrastructureLeasingRejectionDetailsModal
        open={Boolean(rejectionDetailItem)}
        onClose={() => setRejectionDetailItem(null)}
        listingLabel={
          rejectionDetailItem
            ? [listingTitle, rejectionDetailItem.buyerName].filter(Boolean).join(' · ') ||
              rejectionDetailItem.buyerName ||
              'Enquiry'
            : ''
        }
        rejectionReason={rejectionDetailItem?.rejectionReason}
        rejectionComments={rejectionDetailItem?.rejectionComments}
        rejectedAt={rejectionDetailItem?.rejectedAt}
      />
      <InfrastructureLeasingRequirementQuotationModal
        open={Boolean(quotationModalItem)}
        onClose={() => setQuotationModalItem(null)}
        listingId={listingIdNum}
        enquiryId={quotationModalItem?.id}
        buyerName={quotationModalItem?.buyerName}
        listingTitle={listingTitle}
        enquiry={quotationModalItem}
        existingQuotation={quotationModalItem?.sellerRequirementQuotation}
        onSuccess={() => load()}
      />
    </div>
  );
}
